import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Calendar, ShoppingBag, Briefcase, Share2, Shield, Star, CheckCircle } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';

const DiscoverPage = ({ onLogin }) => {
    return (
        <div className="min-h-screen bg-white">
            <Header onLogin={onLogin} />

            <main>
                {/* Hero Section */}
                <div className="relative overflow-hidden bg-linear-to-br from-cabaza-purple/5 to-cabaza-pink/5 pt-24 pb-16 md:pt-32 md:pb-24">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 className="text-4xl md:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight">
                            Plus qu'une simple <span className="text-transparent bg-clip-text bg-linear-to-r from-cabaza-purple to-cabaza-pink">carte de visite</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-10 leading-relaxed">
                            Cabaza est votre assistant personnel pour gérer votre réseau, organiser vos événements et développer votre activité.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link
                                to="/register"
                                className="px-8 py-4 bg-linear-to-r from-cabaza-purple to-cabaza-pink text-white rounded-xl font-bold text-lg hover:shadow-lg hover:scale-105 transition-all duration-300"
                            >
                                Commencer gratuitement
                            </Link>
                            <Link
                                to="/pricing"
                                className="px-8 py-4 bg-white text-gray-700 border-2 border-gray-100 rounded-xl font-bold text-lg hover:border-cabaza-purple/30 hover:bg-gray-50 transition-all duration-300"
                            >
                                Voir les tarifs
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Main Features Grid */}
                <div className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">Tout ce dont vous avez besoin</h2>
                        <div className="w-24 h-1 bg-linear-to-r from-cabaza-purple to-cabaza-pink mx-auto rounded-full"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Feature 1 */}
                        <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                            <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                                <Users className="w-8 h-8 text-cabaza-purple" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Gestion de Contacts</h3>
                            <p className="text-gray-600">
                                Centralisez tous vos contacts. Scannez des cartes de visite papier, créez des fiches détaillées et organisez votre réseau en groupes personnalisés.
                            </p>
                        </div>

                        {/* Feature 2 */}
                        <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                            <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mb-6">
                                <Share2 className="w-8 h-8 text-cabaza-pink" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Partage Facile</h3>
                            <p className="text-gray-600">
                                Partagez votre profil professionnel via QR code, lien direct ou NFC. Vos contacts n'ont pas besoin de l'application pour voir vos infos.
                            </p>
                        </div>

                        {/* Feature 3 */}
                        <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                                <Calendar className="w-8 h-8 text-blue-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Événements & Calendrier</h3>
                            <p className="text-gray-600">
                                Créez des événements, gérez les invitations et synchronisez tout avec votre calendrier. Simplifiez la prise de rendez-vous.
                            </p>
                        </div>

                        {/* Feature 4 */}
                        <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                            <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                                <ShoppingBag className="w-8 h-8 text-green-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Vente de Produits</h3>
                            <p className="text-gray-600">
                                Mettez en avant vos produits à vendre directement sur votre profil. Une mini-boutique accessible à tout votre réseau.
                            </p>
                        </div>

                        {/* Feature 5 */}
                        <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                            <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center mb-6">
                                <Briefcase className="w-8 h-8 text-amber-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Proposez vos Services</h3>
                            <p className="text-gray-600">
                                Indépendant ou entreprise ? Affichez clairement vos services et permettez à vos prospects de vous contacter facilement.
                            </p>
                        </div>

                        {/* Feature 6 */}
                        <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300">
                            <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-6">
                                <Shield className="w-8 h-8 text-indigo-600" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-3">Confidentialité & Sécurité</h3>
                            <p className="text-gray-600">
                                Vos données sont protégées. Vous contrôlez exactement qui voit quelles informations. Conforme RGPD.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Benefits Section */}
                <div className="bg-gray-50 py-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                            <div>
                                <h2 className="text-3xl font-bold text-gray-900 mb-6">Pourquoi choisir Cabaza ?</h2>
                                <div className="space-y-4">
                                    {[
                                        "Une interface moderne et intuitive",
                                        "Accessible partout, tout le temps",
                                        "Réduisez votre empreinte carbone (moins de papier)",
                                        "Mises à jour instantanées de vos informations",
                                        "Support client réactif basé en Belgique"
                                    ].map((item, index) => (
                                        <div key={index} className="flex items-center gap-3">
                                            <CheckCircle className="w-6 h-6 text-green-500 shrink-0" />
                                            <span className="text-lg text-gray-700">{item}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-8">
                                    <Link to="/register" className="text-cabaza-purple font-semibold hover:text-cabaza-pink transition-colors flex items-center gap-2">
                                        Rejoindre Cabaza maintenant →
                                    </Link>
                                </div>
                            </div>
                            <div className="relative">
                                <div className="absolute inset-0 bg-linear-to-tr from-cabaza-purple to-cabaza-pink opacity-10 rounded-3xl transform rotate-3"></div>
                                <div className="bg-white p-8 rounded-3xl shadow-xl relative z-10 border border-gray-100">
                                    <div className="flex items-center gap-4 mb-6">
                                        <div className="w-16 h-16 bg-gray-200 rounded-full overflow-hidden">
                                            {/* Placeholder avatar */}
                                            <div className="w-full h-full bg-linear-to-br from-purple-400 to-pink-400"></div>
                                        </div>
                                        <div>
                                            <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
                                            <div className="h-3 w-24 bg-gray-100 rounded"></div>
                                        </div>
                                    </div>
                                    <div className="space-y-3">
                                        <div className="h-3 w-full bg-gray-100 rounded"></div>
                                        <div className="h-3 w-full bg-gray-100 rounded"></div>
                                        <div className="h-3 w-3/4 bg-gray-100 rounded"></div>
                                    </div>
                                    <div className="mt-6 flex gap-3">
                                        <div className="flex-1 h-10 bg-cabaza-purple rounded-lg opacity-90"></div>
                                        <div className="flex-1 h-10 bg-gray-100 rounded-lg"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <div className="py-24">
                    <div className="max-w-4xl mx-auto px-4 text-center">
                        <div className="bg-linear-to-r from-cabaza-purple to-cabaza-pink rounded-3xl p-12 text-white shadow-2xl relative overflow-hidden">
                            <div className="relative z-10">
                                <h2 className="text-3xl md:text-4xl font-bold mb-6">Prêt à digitaliser votre réseau ?</h2>
                                <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                                    Rejoignez des milliers de professionnels qui font confiance à Cabaza pour gérer leurs contacts.
                                </p>
                                <Link
                                    to="/register"
                                    className="inline-block px-8 py-4 bg-white text-cabaza-purple rounded-xl font-bold text-lg hover:shadow-lg hover:bg-gray-50 transition-all duration-300"
                                >
                                    Créer mon compte gratuit
                                </Link>
                            </div>

                            {/* Decorative circles */}
                            <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                            <div className="absolute bottom-0 right-0 w-64 h-64 bg-white/10 rounded-full translate-x-1/2 translate-y-1/2"></div>
                        </div>
                    </div>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default DiscoverPage;
